import React, { useState, useEffect, useRef } from 'react';
import QuranPageImage from './QuranPageImage';
import QuranAPI from '../services/quranAPI';
import '../styles/NextVerses.css';

// Constante pour le nombre maximum de pages du Coran
const MAX_QURAN_PAGES = 604;

const NextVerses = ({ nextVerses, isVisible, onClose, isLoading }) => {
  // États pour l'accordéon des versets
  const [expandedVerses, setExpandedVerses] = useState({});
  const [displayPageImages, setDisplayPageImages] = useState({});
  const [pageUrls, setPageUrls] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const versesPerPage = 5; // Nombre de versets par page
  
  // États pour l'accordéon de navigation des pages
  const [showPagesAccordion, setShowPagesAccordion] = useState(false);
  const [currentQuranPage, setCurrentQuranPage] = useState(1); // Page actuelle du Coran
  
  // État pour le mode swipe
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const [isMobile, setIsMobile] = useState(false);
  
  // Détecter si l'utilisateur est sur un appareil mobile
  useEffect(() => {
    const checkIfMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Réinitialiser la page courante et afficher le premier verset par défaut
  useEffect(() => {
    setCurrentPage(1);
    // Réinitialiser les images de page chargées pour éviter d'afficher
    // les images du jeu de versets précédent (ex: première question d'un examen)
    setDisplayPageImages({});
    setPageUrls({});
    // Initialiser avec le premier verset déjà affiché si des versets existent
    if (nextVerses.length > 0) {
      setExpandedVerses({ 0: true });
    } else {
      setExpandedVerses({});
    }
  }, [nextVerses]);
  
  // Si nous avons des versets, initialiser la page du Coran à la page du premier verset
  useEffect(() => {
    if (nextVerses.length > 0 && nextVerses[0].page) {
      setCurrentQuranPage(nextVerses[0].page);
    }
  }, [nextVerses]);
  
  // Fonction pour naviguer aux pages précédentes et suivantes du Coran
  const goToPreviousQuranPage = () => {
    if (currentQuranPage > 1) {
      setCurrentQuranPage(currentQuranPage - 1);
    }
  };
  
  const goToNextQuranPage = () => {
    if (currentQuranPage < MAX_QURAN_PAGES) {
      setCurrentQuranPage(currentQuranPage + 1);
    }
  };
  
  // Fonction pour aller à une page spécifique du Coran
  const goToQuranPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= MAX_QURAN_PAGES) {
      setCurrentQuranPage(pageNumber);
    }
  };

  if (!isVisible) {
    return null;
  }

  const toggleVerse = (index) => {
    setExpandedVerses({
      ...expandedVerses,
      [index]: !expandedVerses[index]
    });
  };
  
  // Fonction pour afficher/cacher l'image de la page du Coran
  const togglePageImage = async (index, pageNumber) => {
    if (!displayPageImages[index] && !pageUrls[index]) {
      try {
        const url = await QuranAPI.getQuranPageImage(pageNumber);
        setPageUrls(prev => ({ ...prev, [index]: url }));
      } catch (err) {
        console.error('Erreur chargement image page:', err);
      }
    }
    setDisplayPageImages(prev => ({ ...prev, [index]: !prev[index] }));
  };
  
  // Calculer l'index de début et de fin pour la pagination
  const indexOfLastVerse = currentPage * versesPerPage;
  const indexOfFirstVerse = indexOfLastVerse - versesPerPage;
  const currentVerses = nextVerses.slice(indexOfFirstVerse, indexOfLastVerse);
  const totalPages = Math.ceil(nextVerses.length / versesPerPage);
  
  // Fonction pour changer de page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Fonctions pour naviguer entre les pages
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="next-verses-container">
      <div className="next-verses-header">
        <h3>20 Versets Suivants</h3>
        <button className="close-button" onClick={onClose}>×</button>
      </div>
      
      <div className="accordions-container">
        {/* Accordéon pour les versets */}
        <div className="accordion-tab">
          <div 
            className={`accordion-header ${nextVerses.length > 0 ? 'active' : ''}`}
            onClick={() => nextVerses.length > 0 && setShowPagesAccordion(false)}
          >
            Versets
          </div>
        </div>
        
        {/* Accordéon pour les pages du Coran */}
        <div className="accordion-tab">
          <div 
            className={`accordion-header ${showPagesAccordion ? 'active' : ''}`}
            onClick={() => nextVerses.length > 0 && setShowPagesAccordion(true)}
          >
            Pages du Coran
          </div>
        </div>
      </div>
      
      {isLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement des versets suivants...</p>
        </div>
      ) : nextVerses.length === 0 ? (
        <p className="no-verses">Aucun verset suivant disponible</p>
      ) : (
        <>
          {/* Affichage conditionnel en fonction de l'accordéon sélectionné */}
          {!showPagesAccordion ? (
            // ACCORDÉON 1: VERSETS
            <>
              <div className="pagination-info">
                Affichage de {indexOfFirstVerse + 1}-{Math.min(indexOfLastVerse, nextVerses.length)} sur {nextVerses.length} versets
              </div>
              
              <div className="verses-list">
                {currentVerses.map((verse, index) => {
                  const globalIndex = indexOfFirstVerse + index;
                  return (
                    <div key={`${verse.surah}-${verse.ayah}`} className="verse-item">
                      <div 
                        className="verse-header" 
                        onClick={() => toggleVerse(globalIndex)}
                      >
                        <span className="verse-reference">
                          Sourate {verse.surah}, Verset {verse.ayah}
                        </span>
                        <span className="expand-icon">
                          {expandedVerses[globalIndex] ? '▼' : '▶'}
                        </span>
                      </div>
                      
                      {expandedVerses[globalIndex] && (
                        <div className="verse-content">
                          <div className="verse-arabic">
                            {verse.arabic}
                          </div>
                          {verse.transliteration && (
                            <div className="verse-transliteration">
                              {verse.transliteration}
                            </div>
                          )}
                          <div className="verse-translation">
                            {verse.translation}
                          </div>
                          
                          {verse.page && (
                            <div className="verse-page-info">
                              <p>
                                Ce verset se trouve à la page {verse.page} du Mushaf
                                <button
                                  className="show-page-button"
                                  onClick={e => {
                                    e.stopPropagation();
                                    togglePageImage(globalIndex, verse.page);
                                  }}
                                >
                                  {displayPageImages[globalIndex] ? 'Cacher la page' : 'Voir la page'}
                                </button>
                              </p>
                              
                              {displayPageImages[globalIndex] && (
                                <div 
                                  className={`swipeable-page-container ${isSwiping ? 'swiping' : ''} ${swipeDirection ? `swipe-${swipeDirection}` : ''}`}
                                  onTouchStart={(e) => {
                                    if (isMobile) {
                                      e.stopPropagation();
                                      touchStartX.current = e.touches[0].clientX;
                                      setIsSwiping(true);
                                    }
                                  }}
                                  onTouchMove={(e) => {
                                    if (isMobile && isSwiping) {
                                      e.stopPropagation();
                                      touchEndX.current = e.touches[0].clientX;
                                      const diff = touchStartX.current - touchEndX.current;
                                      
                                      if (Math.abs(diff) > 30) {
                                        setSwipeDirection(diff > 0 ? 'left' : 'right');
                                      } else {
                                        setSwipeDirection(null);
                                      }
                                    }
                                  }}
                                  onTouchEnd={(e) => {
                                    if (isMobile) {
                                      e.stopPropagation();
                                      const diff = touchStartX.current - touchEndX.current;
                                      const currentIndex = globalIndex;
                                      
                                      if (diff > 100) { // Swipe vers la gauche (verset précédent)
                                        const prevIndex = currentIndex - 1;
                                        if (prevIndex >= 0) {
                                          togglePageImage(currentIndex, verse.page); // Fermer l'actuel
                                          if (nextVerses[prevIndex] && nextVerses[prevIndex].page) {
                                            togglePageImage(prevIndex, nextVerses[prevIndex].page); // Ouvrir le précédent
                                            toggleVerse(prevIndex); // Ouvrir le contenu du verset précédent
                                          }
                                        }
                                      } else if (diff < -100) { // Swipe vers la droite (verset suivant)
                                        const nextIndex = currentIndex + 1;
                                        if (nextIndex < nextVerses.length) {
                                          togglePageImage(currentIndex, verse.page); // Fermer l'actuel
                                          if (nextVerses[nextIndex] && nextVerses[nextIndex].page) {
                                            togglePageImage(nextIndex, nextVerses[nextIndex].page); // Ouvrir le suivant
                                            toggleVerse(nextIndex); // Ouvrir le contenu du verset suivant
                                          }
                                        }
                                      }
                                      
                                      // Réinitialiser l'état du swipe
                                      setTimeout(() => {
                                        setIsSwiping(false);
                                        setSwipeDirection(null);
                                      }, 300);
                                    }
                                  }}
                                >
                                  <QuranPageImage
                                    pageNumber={verse.page}
                                    pageUrl={pageUrls[globalIndex]}
                                  />
                                  {isMobile && (
                                    <div className="mobile-swipe-hint">
                                      Glissez pour naviguer entre les pages
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              <div className="pagination-controls">
                <button 
                  className="pagination-button" 
                  onClick={goToPreviousPage} 
                  disabled={currentPage === 1}
                >
                  &laquo; Précédent
                </button>
                
                <div className="pagination-pages">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      className={`page-button ${currentPage === i + 1 ? 'active' : ''}`}
                      onClick={() => paginate(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                
                <button 
                  className="pagination-button" 
                  onClick={goToNextPage} 
                  disabled={currentPage === totalPages}
                >
                  Suivant &raquo;
                </button>
              </div>
            </>
          ) : (
            // ACCORDÉON 2: PAGES DU CORAN
            <div className="quran-pages-container">
              <div className="quran-page-info">
                <h4>Navigation par Pages du Coran</h4>
                <p>Page actuelle: {currentQuranPage} / {MAX_QURAN_PAGES}</p>
              </div>
              {/* Affichage de la page du Coran avec support de swipe */}
              <div 
                className={`quran-page-view ${isSwiping ? 'swiping' : ''} ${swipeDirection ? `swipe-${swipeDirection}` : ''}`}
                onTouchStart={(e) => {
                  if (isMobile) {
                    touchStartX.current = e.touches[0].clientX;
                    setIsSwiping(true);
                  }
                }}
                onTouchMove={(e) => {
                  if (isMobile && isSwiping) {
                    touchEndX.current = e.touches[0].clientX;
                    const diff = touchStartX.current - touchEndX.current;
                    
                    if (Math.abs(diff) > 30) { // Seuil de déplacement
                      setSwipeDirection(diff > 0 ? 'left' : 'right');
                    } else {
                      setSwipeDirection(null);
                    }
                  }
                }}
                onTouchEnd={() => {
                  if (isMobile) {
                    const diff = touchStartX.current - touchEndX.current;
                    
                    if (diff > 100) { // Swipe vers la gauche (page précédente)
                      goToPreviousQuranPage();
                    } else if (diff < -100) { // Swipe vers la droite (page suivante)
                      goToNextQuranPage();
                    }
                    
                    // Réinitialiser l'état du swipe
                    setTimeout(() => {
                      setIsSwiping(false);
                      setSwipeDirection(null);
                    }, 300);
                  }
                }}
              >
                <div className="swipe-indicators">
                  {isMobile && currentQuranPage > 1 && (
                    <div className="swipe-indicator left">
                      <span>←</span>
                    </div>
                  )}
                  {isMobile && currentQuranPage < MAX_QURAN_PAGES && (
                    <div className="swipe-indicator right">
                      <span>→</span>
                    </div>
                  )}
                </div>
                <QuranPageImage
                  pageNumber={currentQuranPage}
                  pageUrl={null}
                />
              </div>
              <div className="quran-page-navigation">
                {/* Champ de saisie pour aller à une page spécifique */}
                <div className="go-to-page">
                  <input 
                    type="number" 
                    min="1" 
                    max={MAX_QURAN_PAGES}
                    value={currentQuranPage}
                    onChange={(e) => {
                      const page = parseInt(e.target.value);
                      if (!isNaN(page)) goToQuranPage(page);
                    }}
                    className="page-input"
                  />
                  <span className="page-total">/ {MAX_QURAN_PAGES}</span>
                </div>
              
                {/* Boutons de navigation */}
                <div className="page-navigation-buttons">
                  <button 
                    className="nav-button" 
                    onClick={goToPreviousQuranPage}
                    disabled={currentQuranPage === 1}
                  >
                    &laquo; Page précédente
                  </button>
                  
                  <button 
                    className="nav-button" 
                    onClick={goToNextQuranPage}
                    disabled={currentQuranPage === MAX_QURAN_PAGES}
                  >
                    Page suivante &raquo;
                  </button>
                </div>
              </div>
              
              {/* Liste des pages des versets actuels pour un accès rapide */}
              <div className="verse-pages-list">
                <h5>Pages des versets actuels:</h5>
                <div className="verse-page-buttons">
                  {[...new Set(nextVerses.map(verse => verse.page))]
                    .filter(page => page) // Filtrer les pages null/undefined
                    .sort((a, b) => a - b) // Trier par ordre croissant
                    .map(page => (
                      <button 
                        key={page} 
                        className={`verse-page-button ${currentQuranPage === page ? 'active' : ''}`}
                        onClick={() => goToQuranPage(page)}
                      >
                        {page}
                      </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
      
      <div className="next-verses-footer">
        <p>Cliquez sur un verset pour voir son contenu</p>
      </div>
    </div>
  );
};

export default NextVerses;
